import { useMemo } from "react";
import { motion, useReducedMotion } from "motion/react";

interface Star {
  top: string;
  left: string;
  size: number;
  base: number; // base opacity
  twinkle: number; // seconds
  delay: number;
}

/** Night backdrop: a field of twinkling stars + the occasional shooting star. */
export function Stars() {
  const reduced = useReducedMotion();

  const stars = useMemo<Star[]>(
    () =>
      Array.from({ length: 70 }, () => ({
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        size: Math.random() < 0.18 ? 2.5 : 1.4,
        base: 0.35 + Math.random() * 0.5,
        twinkle: 2 + Math.random() * 4,
        delay: -Math.random() * 6,
      })),
    [],
  );

  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
      {stars.map((s, i) => (
        <motion.span
          key={i}
          style={{
            position: "absolute",
            top: s.top,
            left: s.left,
            width: s.size,
            height: s.size,
            borderRadius: "50%",
            background: "#fdfdff",
            boxShadow: "0 0 4px rgba(255,255,255,.7)",
          }}
          animate={reduced ? { opacity: s.base } : { opacity: [s.base * 0.4, s.base, s.base * 0.4] }}
          transition={reduced ? { duration: 0 } : { duration: s.twinkle, delay: s.delay, ease: "easeInOut", repeat: Infinity }}
        />
      ))}

      {!reduced &&
        [0, 1].map((i) => (
          // Static wrapper holds the diagonal tilt; inner node only translates,
          // so the streak actually flies across instead of freezing rotated.
          <div
            key={`shoot-${i}`}
            style={{
              position: "absolute",
              top: `${10 + i * 26}%`,
              left: 0,
              width: "100%",
              height: 0,
              transform: `rotate(${12 + i * 5}deg)`,
              transformOrigin: "left center",
            }}
          >
            <motion.div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: 120,
                height: 2,
                borderRadius: 2,
                background: "linear-gradient(90deg, transparent, #ffffff)",
                willChange: "transform, opacity",
              }}
              initial={{ x: "-18vw", opacity: 0 }}
              animate={{ x: ["-18vw", "118vw"], opacity: [0, 1, 1, 0] }}
              transition={{ duration: 1.4, ease: "easeIn", repeat: Infinity, repeatDelay: 9 + i * 6, delay: 3 + i * 5 }}
            />
          </div>
        ))}
    </div>
  );
}
